import React, { useCallback, useEffect, useRef, useState } from "react";
import { Route, Switch, useLocation, withRouter, matchPath } from "react-router-dom";
import _routes, { defaultPath, libraryRoutes } from "./cores/routes";
import RouteWithSubRoutes from "./components/routeWithSubRoutes";
import RoutesWrapper from "@components_v2/layout/RoutesWrapper";
import CustomRoutes from "@components_v2/layout/CustomRoutes";
import Login from "./routes/providers/Login";
import ExchangeCode from "./routes/saml/exchangeCode";

import LoginRedirection from "./routes/login/LoginRedirection";
import InitiateLXHausys from "./routes/login/InitiateLXHausys";
import ConfirmUser from "./routes/confirm-user/ConfirmUser";
import Error404 from "./routes/errors/Error404";
import Error500 from "./routes/errors/Error500";
import InvalidUser from "./routes/errors/InvalidUser";
import MobileNotSupported from "./routes/errors/MobileNotSupported";
import Welcome from "./routes/welcome/Welcome";
import WelcomeLxHausys from "@routes/saml/welcome-lx-hausys";
import theme, { narrowTheme } from "./styles/theme";
import GetAccessTokenPopup from "./routes/oauth2/cpd/GetAccessTokenPopup";

import {
    LayoutForError,
    LayoutForLxWelcome,
    LayoutForNoting,
    LayoutForPreview,
    LayoutForViewer,
} from "./components/layout";
import CacheBuster from "./CacheBuster";
import config from "./config/config";
import "url-search-params-polyfill"; //new URLSearchParams polyfill
import { CssBaseline, PDCThemeProvider } from "@mzc-pdc/ui";
import { useMediaQuery } from "@mui/material";
import "./extensions/array-extensions";

import { siteMode } from "./cores/siteMode";
import { checkIsMobile } from "./utils/checkmobile";
import menuService from "@services/menuService";
import GlobalStyle from "@styles/global";
import LayoutForComponentV2 from "@components_v2/layout/LayoutForComponentV2";
import { LayoutForAdminV2 } from "@components_v2/layout";
import LayoutForComponentWithoutNav from "@components_v2/layout/LayoutForComponentWithoutNav";
import i18n from "@lib/i18n";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "@react-loadable/revised";
import LayoutForOnlyComponent from "@components_v2/layout/LayoutForOnlyCompnent";
import { isDev, isLocal, isQA } from "@cores/enviromentExtension";
import { UPLOAD_FILE_LOCATION_TYPE, UPLOAD_LOCATION } from "@constants";
import * as jobStatus from "@cores/jobStatus";
import { cancelActions } from "@modules/uploadQueue";
import { cancelTranscodeAllSync } from "@modules/upload";
import Loader from "@components/loader";
import EntryConsole from "@routes/entryConsole/entryConsole";
import Stage from "@routes/stage/Stage";
import UserAgreement from "@routes/providers/userAgreement";
import LoginPopup from "@routes/snsLogin/LoginPopup";
import AccessTokenBridge from "@routes/oauth2/access-token-bridge/AccessTokenBridge";
import LayoutForDefaultLogo from "@components/layout/LayoutForDefaultLogo";
import WebSocketProvider from "@routes/providers/webSocketProvider";
import { useHistory, useParams } from "react-router-dom";
import { SnackbarProvider } from "@components_v2/snackbar-provider";

const LoadableLibraryAssetDetail = Loadable({
    loader: () => import("./routes/asset/detail-library-asset"),
    loading: () => null,
});

const App = ({ location }) => {
    const isCms = siteMode.get() === "CMS";
    const isMobile = checkIsMobile();
    const isLxhausys = window.location.hostname.indexOf("lxhausys.") > -1;
    const isMobileWidth = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileRestricted = (isLocal || isDev || isQA || isLxhausys) && (isMobile || isMobileWidth);
    const { pathname, search } = useLocation();
    const history = useHistory();
    const { id } = useParams();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const dispatch = useDispatch();
    const monitorUnloadBeforeUploadingRef = useRef(null);

    const commonComments = useSelector((state) => state.commonComments);
    const comments = useSelector((state) => state.comments);
    const upload = useSelector((state) => state.upload);
    const uploadQueue = useSelector((state) => state.uploadQueue);
    const downloadJobs = useSelector((state) => state.downloadJobs);

    const uploadRef = useRef(upload);
    const uploadQueueRef = useRef(uploadQueue);
    const downloadJobsRef = useRef(downloadJobs);
    const settings = useSelector((state) => state.settings);
    const user = useSelector((state) => state.user);
    const project = useSelector((state) => state.project);
    const global = useSelector(({ global }) => global);

    const isEntryConsole =
        pathname === "/" ||
        pathname === "/library/" ||
        !!matchPath(pathname, { path: `${defaultPath}/`, exact: true }) ||
        !!matchPath(pathname, { path: `/library${defaultPath}/`, exact: true });
    const isAdminSetting = location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1;
    const [routeType, setRouteType] = useState(null);

    let Layout = LayoutForComponentV2;
    if (user.pending || user.stages.stagePending || user.stages.allProjectsPending) {
        Layout = LayoutForNoting;
    }

    if (window.opener && window.name && window.name.indexOf("popup") > -1) {
        Layout = LayoutForPreview;
    } else if (location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1 && !projectId) {
        Layout = LayoutForAdminV2;
    } else if (
        location.pathname.indexOf("/welcome-saml-test") !== -1 ||
        (location.pathname.indexOf("/welcome") !== -1 && window.location.hostname.indexOf("lxhausys.") !== -1)
    ) {
        Layout = LayoutForLxWelcome;
    } else if (
        location.pathname.indexOf("/welcome") !== -1 ||
        location.pathname.indexOf("/auth-login") !== -1 ||
        location.pathname.indexOf("/access-token-bridge") !== -1 ||
        location.pathname === "/spaces"
    ) {
        Layout = LayoutForDefaultLogo;
    } else if (
        location.pathname.indexOf("/megazone/login") !== -1 ||
        location.pathname === "/library/spaces" ||
        location.pathname.indexOf("/error/invaliduser") !== -1 ||
        location.pathname.indexOf("/confirmUser") !== -1 ||
        location.pathname.indexOf("/jwt/exchange") !== -1 ||
        location.pathname.indexOf("/initiate-lxhausys") !== -1
    ) {
        Layout = LayoutForNoting;
    } else if (location.pathname.indexOf("/error/") !== -1) {
        if (location.pathname.indexOf("/accessdenied") !== -1 || location.pathname.indexOf("/deleted") !== -1) {
            Layout = LayoutForViewer;
        } else {
            Layout = LayoutForError;
        }
    } else if (
        location.pathname.indexOf("preview") !== -1 ||
        location.pathname.indexOf("/player") !== -1 ||
        location.pathname.indexOf("/play") !== -1
    ) {
        Layout = LayoutForPreview;
    } else if (location.pathname.indexOf("/snsLogin") !== -1 || location.pathname.indexOf("/cloudplex/oauth2") !== -1) {
        Layout = LayoutForPreview;
    }
    //NOTE: 현재 사용하지 않아서 주석처리함
    // else if (location.pathname.indexOf("/images-viewer") !== -1) {
    //     Layout = LayoutForViewer;
    // }
    else if (
        location.pathname.indexOf("customMenuSets/common/") !== -1 ||
        location.pathname === "/" ||
        location.pathname === "/library/" ||
        !!matchPath(location.pathname, { path: `${defaultPath}/`, exact: true }) ||
        !!matchPath(location.pathname, { path: `/library${defaultPath}/`, exact: true })
    ) {
        Layout = LayoutForOnlyComponent;
    } else if (
        ["/collections", "/assets/", "/archived/", "/contents/", "/storage/"].findIndex(
            (path) => location.pathname.indexOf(path) !== -1,
        ) >= 0
    ) {
        // Note: container 100vh height를 제거한 layout 적용
        Layout = LayoutForComponentV2;
    } else if (location.pathname.indexOf("/vocabularies") !== -1) {
        Layout = LayoutForComponentWithoutNav;
    } else if (location.pathname.indexOf("/event-triggers") !== -1) {
        Layout = LayoutForComponentV2;
    }

    const loadThirdPartyLib = (elementType, params) => {
        const element = document.createElement(elementType);
        Object.keys(params).some((key) => {
            element[key] = params[key];
        });
        document.body.appendChild(element);
    };

    const handleWindow = () => {
        let body = document.querySelector("body");
        if (window.innerWidth > body.clientWidth + 5) {
            body.classList.add("has-scrollbar");
            body.setAttribute("style", "--scroll-bar-width: " + (window.innerWidth - body.clientWidth) + "px");
        } else {
            body.classList.remove("has-scrollbar");
        }
    };

    useEffect(() => {
        loadThirdPartyLib("script", {
            src: `${config.PLAYER_URL}/videoJs/resources/scripts/cloudplex.media.player.js`,
        });
        const handleContextMenu = (e) => {
            // 우클릭 방지!
            e.preventDefault();
        };

        if (!isLocal) {
            window.addEventListener("contextmenu", handleContextMenu);
        }
        window.addEventListener("resize", handleWindow);
        return () => {
            window.removeEventListener("resize", handleWindow);
            if (!isLocal) {
                window.removeEventListener("contextmenu", handleContextMenu);
            }
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            window.scrollTo(0, 0);
        }
    }, [pathname]);

    function getRoutesBySiteMode() {
        if (siteMode.get() === "CMS") {
            return libraryRoutes.map((r) => {
                if (r.name === "Asset Detail") {
                    r.component = LoadableLibraryAssetDetail; //Note: 라이브러리의 경우 에셋 상세 페이지 컴포넌트가 별도로 있어서 예외처리
                }
                return {
                    ...r,
                    path: `/library${r.path}`,
                    redirectPath: `/library${r.redirectPath}`,
                };
            });
        }

        return _routes;
    }

    const maches = useMediaQuery(theme.breakpoints.down("md"));

    useEffect(() => {
        const uploadingDomainComments = Object.values(comments.uploadingComments).flat();
        const uploadingCommonComments = Object.values(commonComments.uploadingComments).flat();
        const isUploadingCommentsExisted = uploadingDomainComments.length > 0 || uploadingCommonComments.length > 0;

        if (monitorUnloadBeforeUploadingRef.current === null && isUploadingCommentsExisted) {
            monitorUnloadBeforeUploadingRef.current = (e) => {
                e.preventDefault();
                const message = i18n.t(`common::msg::${"There is work in progress. Would you really leave the page?"}`);
                (e || window.event).returnValue = message;

                return message;
            };
            window.addEventListener("beforeunload", monitorUnloadBeforeUploadingRef.current);
        } else if (monitorUnloadBeforeUploadingRef.current && !isUploadingCommentsExisted) {
            window.removeEventListener("beforeunload", monitorUnloadBeforeUploadingRef.current);
            monitorUnloadBeforeUploadingRef.current = null;
        }
    }, [commonComments.uploadingComments, comments.uploadingComments]);

    useEffect(() => {
        uploadRef.current = upload;
    }, [upload]);
    useEffect(() => {
        uploadQueueRef.current = uploadQueue;
    }, [uploadQueue]);
    useEffect(() => {
        downloadJobsRef.current = downloadJobs;
    }, [downloadJobs]);

    useEffect(() => {
        const locationType = UPLOAD_FILE_LOCATION_TYPE.LOCAL || "Local";

        //Note: 브라우저가 직접 전송하는 업로드 큐. 페이지를 닫으면 그 자리에서 끊기므로 이탈 경고 대상이다.
        //판정식은 hooks/useMyJobs.js 의 isJobProcessing 이 두 큐에 쓰는 것과 같다(헤더 My Jobs 아이콘이 그 값을 쓴다).
        const browserUploadLocations = [UPLOAD_FILE_LOCATION_TYPE.LOCAL, UPLOAD_LOCATION.CREATE_ASSET];

        const hasProcessingUploadJob = () =>
            browserUploadLocations.some((queueLocation) =>
                (uploadQueueRef.current[queueLocation] || []).some((v) => !jobStatus.isComplete(v.status)),
            );

        const getCancelTargetTranscodingJobs = () =>
            uploadRef.current.jobs.data.filter(
                (v) => jobStatus.getValuesLowerThan(jobStatus.values.SUBMITTED).indexOf(v.status) > -1,
            );

        const cancelTranscodingJobs = () => {
            const cancelTargetJobs = getCancelTargetTranscodingJobs();
            if (cancelTargetJobs.length > 0) {
                dispatch(cancelTranscodeAllSync(cancelTargetJobs));
            }
        };

        const cancelUploadingJobs = () => {
            const cancelUploadJobs = (uploadQueueRef.current[locationType] || []).filter(
                (v) => !jobStatus.isComplete(v.status),
            );
            if (cancelUploadJobs.length > 0) {
                cancelUploadJobs.forEach((item) => {
                    dispatch(cancelActions(locationType, item.key));
                });
            }
        };

        const handleBeforeunload = (e) => {
            if (downloadJobsRef.current.beforeUnloadCheck > 0) return;

            //Note: 경고 대상 != 취소 대상. 자산 생성 업로드는 경고만 하고 handleUnload 에서 취소하지 않는다
            //(cancelActions 는 저장소 업로드 전용이라 bucketPath 가 없는 자산 생성 큐에서는 동작하지 않는다).
            const hasWarningTargetJob = getCancelTargetTranscodingJobs().length > 0 || hasProcessingUploadJob();

            if (hasWarningTargetJob) {
                //Note: 경고창 문구는 브라우저가 통제한다 — Chrome 51+·Firefox 44+·Safari 9.1+ 는 커스텀 문구를 무시하고
                //자체 문구를 띄운다. 그래서 i18n 문구를 넘기지 않는다(넘겨도 화면에 나오지 않아 오해만 만든다).
                //표준 트리거는 preventDefault 이고, returnValue 대입은 레거시 브라우저(Chrome/Edge < 119) 호환용이다.
                e.preventDefault();
                e.returnValue = true;
            }
        };

        const handleUnload = () => {
            cancelUploadingJobs();
            cancelTranscodingJobs();
        };

        window.addEventListener("beforeunload", handleBeforeunload);
        window.addEventListener("pagehide", handleUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeunload);
            window.removeEventListener("pagehide", handleUnload);
        };
    }, [dispatch]);

    const welcomeComponent = window.location.hostname.indexOf("lxhausys.") !== -1 ? WelcomeLxHausys : Welcome;

    const getRoutes = useCallback(() => {
        try {
            if (settings.pending || !settings.project?.MenuName) {
                setRouteType("loader");
                return;
            }

            const searchParams = new URLSearchParams(search);
            const routeTypeFromQuery = searchParams.get("routeType");

            if (settings.project?.MenuName?.selectMenu?.value === "custom") {
                setRouteType("custom");
            } else {
                setRouteType("default");
            }
        } catch (error) {
            console.error("Error generating routes:", error);
            setRouteType("error");
        }
    }, [settings.project, settings.pending, isEntryConsole]);

    const validateCurrentUrl = useCallback(() => {
        // search에서 linkFromNoti 값 추출
        const searchParams = new URLSearchParams(search);
        const linkFromNoti = searchParams.get("linkFromNoti");

        if (
            linkFromNoti &&
            settings.project.MenuName?.selectMenu?.value === "custom" &&
            pathname.indexOf("/customMenuSets") < 0
        ) {
            let resourceId = null;
            const routePatterns = [
                "/spaces/:spaceId/projects/:projectId/assets/collections/:id",
                "/spaces/:spaceId/projects/:projectId/assets/assets/:id",
                "/spaces/:spaceId/projects/:projectId/contents/:id",
                // 다른 패턴들 추가
            ];

            for (const pattern of routePatterns) {
                const match = matchPath(pathname, {
                    path: pattern,
                    exact: false,
                    strict: false,
                });

                if (match && match.params && match.params.id) {
                    resourceId = match.params.id;
                    break;
                }
            }

            const _search = resourceId ? `${search}&resourceId=${resourceId}` : search;
            history.replace(`/${_search}`);
            //TODO : 엔트리콘솔로 이동시키기 - 주소만 잘 보내면 됨..
        }

        getRoutes();
    }, [settings.project, settings.pending, isEntryConsole, isAdminSetting, pathname, search, id]);

    useEffect(() => {
        if (isAdminSetting) {
            setRouteType("default");
            return;
        }
        //Note: 엔트리 콘솔에 있는 동안은 라우트 타입이 미정이다. 여기서 비우지 않으면 직전 값이 남는다 —
        //관리자에서 「프로젝트로 돌아가기」로 온 경우 "default" 가 남아, 엔트리 콘솔이 SPA 로 이동한
        //첫 렌더에서 커스텀 메뉴 경로를 default 라우트로 맞춰 보고 Page not found 가 잠깐 뜬다.
        if (isEntryConsole) {
            setRouteType(null);
            return;
        }
        if (!settings.project || settings.pending) return;

        validateCurrentUrl();
    }, [settings.project, settings.pending, isEntryConsole, isAdminSetting]);

    if (isMobileRestricted) {
        return (
            <PDCThemeProvider theme={theme}>
                <CssBaseline />
                {!isCms && <GlobalStyle />}
                <CacheBuster>
                    {({ loading, isLatestVersion, refreshCacheAndReload }) => {
                        if (loading) return null;
                        if (!loading && !isLatestVersion) {
                            refreshCacheAndReload();
                        }
                        return <MobileNotSupported />;
                    }}
                </CacheBuster>
            </PDCThemeProvider>
        );
    }

    return (
        <PDCThemeProvider theme={maches ? narrowTheme : theme}>
            <SnackbarProvider>
                <CssBaseline />
                {!isCms && <GlobalStyle />}
                <CacheBuster>
                    {({ loading, isLatestVersion, refreshCacheAndReload }) => {
                        if (loading) return null;
                        if (!loading && !isLatestVersion) {
                            refreshCacheAndReload();
                        }
                        return (
                            <WebSocketProvider>
                                <Layout routeType={routeType}>
                                    <Switch>
                                        <Route exact={true} path={"/welcome"} component={welcomeComponent} />
                                        <Route exact={true} path={"/library/welcome"} component={welcomeComponent} />
                                        <Route exact={true} path={"/welcome-saml-test"} component={WelcomeLxHausys} />
                                        <Route exact={true} path={"/megazone/login"} component={Login} />
                                        <Route exact={true} path={"/jwt/exchange"} component={ExchangeCode} />
                                        <Route exact={true} path={"/confirmUser"} component={ConfirmUser} />
                                        <Route
                                            exact={true}
                                            path={"/cloudplex/oauth2"}
                                            component={GetAccessTokenPopup}
                                        />
                                        <Route exact={true} path={"/login"} component={LoginRedirection} />
                                        <Route exact={true} path={"/library/login"} component={LoginRedirection} />
                                        <Route exact={true} path={"/snsLogin/:snsKind"} component={LoginPopup} />
                                        <Route
                                            exact={true}
                                            path={"/snsLogin/:snsKind/callback"}
                                            component={LoginPopup}
                                        />
                                        <Route exact={true} path={"/initiate-lxhausys"} component={InitiateLXHausys} />
                                        <Route exact={true} path={"/error/404"} component={Error404} />
                                        <Route exact={true} path={"/error/500"} component={Error500} />
                                        <Route exact={true} path={"/error/invaliduser"} component={InvalidUser} />
                                        <RoutesWrapper>
                                            <Route exact={true} path={"/"} component={EntryConsole} />
                                            <Route
                                                exact={true}
                                                path={"/access-token-bridge"}
                                                component={AccessTokenBridge}
                                            />
                                            <Route exact={true} path={"/library/"} component={EntryConsole} />
                                            <Route exact={true} path={`${defaultPath}/`} component={EntryConsole} />
                                            <Route
                                                exact={true}
                                                path={`/library${defaultPath}/`}
                                                component={EntryConsole}
                                            />
                                            <Route
                                                path={"/spaces"}
                                                exact={true}
                                                render={(props) => (
                                                    <UserAgreement>
                                                        <Stage />
                                                    </UserAgreement>
                                                )}
                                            />
                                            <Route
                                                path={"/library/spaces"}
                                                exact={true}
                                                render={(props) => (
                                                    <UserAgreement>
                                                        <Stage />
                                                    </UserAgreement>
                                                )}
                                            />
                                            {routeType === "error" && <Error404 />}
                                            {routeType === "loader" && <Loader />}
                                            {routeType &&
                                                !isEntryConsole &&
                                                ["custom", "default"].includes(routeType) &&
                                                (routeType === "custom" ? (
                                                    !global.isAgreementRequired && <CustomRoutes />
                                                ) : (
                                                    <Switch>
                                                        {getRoutesBySiteMode().map((route, i) => (
                                                            <RouteWithSubRoutes key={i} {...route} />
                                                        ))}
                                                        <Route path={"*"} component={Error404} />
                                                    </Switch>
                                                ))}
                                        </RoutesWrapper>
                                    </Switch>
                                </Layout>
                            </WebSocketProvider>
                        );
                    }}
                </CacheBuster>
            </SnackbarProvider>
        </PDCThemeProvider>
    );
};

export default withRouter(App);
